// Drop zone for the Upload pane's "Upload files" tab. Exactly two ways in: drag
// image files onto it, or pick them with the "Choose images…" file picker
// (multi-select). It only hands back the raw Files — format checks and the JPEG
// conversion happen in UploadPane / imageFile.ts.

import { useEffect, useRef, useState, type DragEvent } from "react";
import { ACCEPT_IMAGE_FILES, SUPPORTED_FORMATS_TEXT } from "../imageFile";

export interface FileDropZoneProps {
  onFiles: (files: File[]) => void;
}

const carriesFiles = (dt: DataTransfer | null) => !!dt && Array.from(dt.types).includes("Files");

export function FileDropZone({ onFiles }: FileDropZoneProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [over, setOver] = useState(false);
  // dragenter/dragleave also fire for the zone's children; count them so the
  // highlight doesn't flicker while the pointer crosses the button/text.
  const depth = useRef(0);

  // A file dropped anywhere OUTSIDE the zone would make the browser open it,
  // navigating away from the app (and dropping any uploads still in flight).
  useEffect(() => {
    const guard = (e: globalThis.DragEvent) => {
      if (e.defaultPrevented || !carriesFiles(e.dataTransfer)) return; // the zone handled it
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "none";
    };
    window.addEventListener("dragover", guard);
    window.addEventListener("drop", guard);
    return () => {
      window.removeEventListener("dragover", guard);
      window.removeEventListener("drop", guard);
    };
  }, []);

  const onDragEnter = (e: DragEvent) => {
    if (!carriesFiles(e.dataTransfer)) return;
    e.preventDefault();
    depth.current += 1;
    setOver(true);
  };
  const onDragOver = (e: DragEvent) => {
    if (!carriesFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const onDragLeave = () => {
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setOver(false);
  };
  const onDrop = (e: DragEvent) => {
    if (!carriesFiles(e.dataTransfer)) return;
    e.preventDefault();
    depth.current = 0;
    setOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length) onFiles(files);
  };

  return (
    <div
      className={`drop-zone${over ? " drop-zone--over" : ""}`}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <svg className="drop-zone__icon" viewBox="0 0 48 48" aria-hidden="true">
        <path d="M24 31V9m0 0-8 8m8-8 8 8" />
        <path d="M9 29v8a3 3 0 0 0 3 3h24a3 3 0 0 0 3-3v-8" />
      </svg>
      <p className="drop-zone__title">{over ? "Drop to upload" : "Drag images here"}</p>
      <span className="drop-zone__or">or</span>
      <button
        type="button"
        className="btn btn--primary btn--large"
        onClick={() => inputRef.current?.click()}
      >
        Choose images…
      </button>
      <p className="drop-zone__formats">{SUPPORTED_FORMATS_TEXT} · several at once is fine</p>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_IMAGE_FILES}
        multiple
        hidden
        onChange={(e) => {
          const files = Array.from(e.currentTarget.files ?? []);
          e.currentTarget.value = ""; // so choosing the same file again still fires change
          if (files.length) onFiles(files);
        }}
      />
    </div>
  );
}
